<script setup>

import helpers from "@/lib/helpers";
import {onMounted, ref, computed} from "vue";

import ReportGliLookup from "@/components/report/outputs/report-gli-lookup.vue";
import ReportSerialized from "@/components/report/outputs/report-serialized.vue";
import ReportDrill from "@/components/report/outputs/report-drill.vue";
import ReportListTool from "@/components/report/outputs/report-list-tool.vue";
import ReportOmsRf from "@/components/report/outputs/report-oms-rf.vue";
import ReportTagEdit from "@/components/report/outputs/report-tag-edit.vue";
import ReportPhoto from "@/components/report/outputs/report-photo.vue";
import store from "@/store";
import accounting from "accounting";
import getSymbolFromCurrency from "currency-symbol-map";

const props = defineProps({
  id: null,
  form: Object,
  saved: null,
  hide_cols: Array,
  height: Number,
  responsive: Boolean,
  nosum: Boolean
})

const emit = defineEmits([
  'loaded', 'error', 'meta-error'
])

const outputs = {
  ReportGliLookup,
  ReportDrill,
  ReportListTool,
  ReportSerialized,
  ReportOmsRf,
  ReportTagEdit,
  ReportPhoto
}
const result = ref()
const fields = ref([])
let meta = null
let query = null

const formatter = (v, key) => {
  if (v && v.display !== undefined)
    v = v.display

  if (meta.output[key]?.format === 'Money')
    v = (v === null || v === undefined) ? '' : accounting.formatMoney(v, getSymbolFromCurrency('USD'), 2)

  return v
}

/** Reports wrap a rendered column as {display, extra, raw}; this unwraps it. */
const cell_text = (row, key) => {
  const v = row ? row[key] : null

  if (v === null || v === undefined)
    return ''

  return typeof v === 'object' ? String(v.display ?? v.raw ?? '') : String(v)
}

const fields_init = (f) => {
  return Object.entries(f).map(([key, value]) => {
    const tdClass = []
    const thClass = []

    if (value['format'] === 'Number' || value['format'] === 'Money') {
      tdClass.push('text-right')
      thClass.push('text-right')
    }

    if (value.nowrap)
      tdClass.push('text-nowrap')

    return {
      key,
      hide: value.hide,
      label: value.label || key,
      webonly: value.webonly,
      formatter,
      sortable: !value.nosort,
      tdClass: tdClass.join(' '),
      thClass: thClass.join(' '),
      output: value.output,
      // rendered under the row rather than as a column of its own
      subrow: value.subrow,
      html: value.html,
    }
  })
}

onMounted(async () => {
  let form
  try {
    meta = await helpers.asyncAction(`/api/report/${props.id}/meta`, 'GET')
  } catch(e) {
    emit('meta-error', e)
    return
  }
  form = props.form

  const inputs = meta.input
      .flatMap(f => {
        return f.type === 'Group' ? f.inputs : [f]
      })
      .filter(f => !!f.name)
      .map(f => f.name);

  fields.value = fields_init(meta.output)

  query = Object.keys(form)
      .filter(key => inputs.includes(key))
      .reduce((obj, key) => {
        obj[key] = form[key];
        return obj;
      }, {});

  try {
    const res = await helpers.asyncAction(`/api/report/${meta.id}`, 'GET', {data: query})
    const built = fields_init(res.meta.output)

    // b-table shows a row's details when the row itself says so, and in Vue 2
    // that flag has to exist before the object is made reactive - so it is set
    // here rather than after result.value is assigned.
    const subs = built.filter(f => f.subrow && !f.hide)

    if (subs.length)
      for (const row of (res.data || []))
        if (subs.some(f => cell_text(row, f.key).trim() !== ''))
          row._showDetails = true

    fields.value = built
    result.value = res
    emit('loaded', {fields: fields.value, query: query, meta: res.meta, data: res.data})
  } catch (e) {
    console.log('exception', e)
    emit('error', {fields, query, meta, state: e.fields})
  }
})

/**
 * The row counter.
 *
 * Display-only: it is deliberately not in fields.value, so it stays out of the
 * Show/Hide Columns chooser and out of the xlsx (which the server builds from
 * the report's own output(), not from this list). Not sortable - its whole job
 * is to number the rows in the order they are currently shown.
 */
const counterField = {
  key: '_rownum',
  label: '#',
  sortable: false,
  thClass: 'text-right text-muted',
  tdClass: 'text-right text-muted',
  thStyle: {width: '1%', whiteSpace: 'nowrap'},
}

const subrow_fields = computed(() => (fields.value || []).filter(f => f.subrow && !f.hide))

const fields_filtered = computed(() => {
  const shown = fields.value.filter(f => {
    if (f.hide || f.subrow)
      return false;

    if (Array.isArray(props.hide_cols) && props.hide_cols.includes(f.key))
      return false

    return !(f.webonly && store.state.printing);
  })

  return shown.length ? [counterField, ...shown] : shown
})

/**
 * Rows an output component has marked as dealt with. Set by the listing tool
 * when its popup closes, so a worklist shows how far through it the user is.
 */
const rowClass = (item) => (item && item._listed) ? 'table-success' : ''

const sort_field = (f) => f.sort !== undefined ? f.sort : (f.raw !== undefined ? f.raw : f);
const sorter = (arow, brow, field, sortDesc, formatter, compareOptions, compareLocale) => {
  const a = arow[field] && sort_field(arow[field])
  const b = brow[field] && sort_field(brow[field])

  if (
      (typeof a === 'number' && typeof b === 'number') ||
      (a instanceof Date && b instanceof Date)
  ) {
    // If both compared fields are native numbers or both are native dates
    return a < b ? -1 : a > b ? 1 : 0
  } else {
    // Otherwise stringify the field data and use String.prototype.localeCompare
    return toString(a).localeCompare(toString(b), compareLocale, compareOptions)
  }
}

const toString = (value) => {
  if (value === null || typeof value === 'undefined') {
    return ''
  } else {
    return String(value)
  }
}

</script>

<template>
  <b-table
      v-if="!!result"
      :items="result.data"
      foot-row-variant=""
      :foot-clone="!nosum && result.sum && result.data && (result.data.length > 1) && !!Object.keys(result.sum).length"
      :sticky-header="($store.state.printing || !height) ? false : `${height}px`"
      :fields="fields_filtered"
      :sort-compare="sorter"
      :tbody-tr-class="rowClass"
      no-footer-sorting
      head-variant="light"
      thead-class="bg-white pt-3"
      :responsive="responsive"
  >
    <template #row-details="{ item }">
      <div v-for="f in subrow_fields" :key="f.key" class="px-2 pb-2 small">
        <!-- html only where the report asks for it; everything else is text -->
        <div v-if="f.html" class="report-subrow" v-html="cell_text(item, f.key)"/>
        <div v-else class="report-subrow text-break">{{ cell_text(item, f.key) }}</div>
      </div>
    </template>

    <template #cell(_rownum)="{ index }">{{ index + 1 }}</template>

    <!-- the sum row has nothing to add up here, so it carries the row total -->
    <template #foot(_rownum)>{{ result.data ? result.data.length : '' }}</template>

    <template #cell()="{value, field, unformatted, item}">
      <component v-if="!!outputs[field.output]" :is="outputs[field.output]" :value="unformatted" :item="item"/>
      <template v-if="!outputs[field.output]">{{ value }}</template>
    </template>

    <template #foot()="{ field }">
      {{ formatter(result.sum[field.key], field.key) }}
    </template>
  </b-table>
</template>

<style scoped>
/* listing bodies are full eBay descriptions - keep one from taking the page */
.report-subrow {
  max-height: 14rem;
  overflow-y: auto;
}

.report-subrow ::v-deep img {
  max-width: 100%;
  height: auto;
}
</style>